<template>
    <v-form ref="form" v-model="valid" class="pb-5">
        <v-container>
            <v-row v-if="ownerCreationForm">
                <h3 class="col-12 title-class">Insira seus dados</h3>
                <v-col cols="12" md="6">
                    <v-text-field
                        outlined
                        v-model="assignedName"
                        :rules="nameRules"
                        label="Nome"
                        required
                    ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        outlined
                        v-model="assignedEmail"
                        :rules="emailRules"
                        label="Email"
                        required
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <h3 class="col-12 title-class">Dados do hotel</h3>
                <v-col cols="12" :md="isEditingHotel ? '12' : '8'">
                    <v-text-field
                        outlined
                        v-model="hotelName"
                        :rules="nameRules"
                        :error="hotelNameError"
                        :error-messages="hotelNameErrors"
                        @blur="validateHotelName"
                        label="Nome do Hotel"
                        required
                    ></v-text-field>
                </v-col>
                <v-col v-if="hotel" cols="12" md="6" class="active">
                    <div>
                        <p class="active-label">Status</p>
                    </div>
                    <v-btn-toggle
                        v-model="active"
                        mandatory
                        class="btnToggle"
                    >
                        <v-btn class="mt-2" width="50%" :value="1" :color="activeBtnColor">Ativo</v-btn>
                        <v-btn class="mt-2" width="50%" :value="0" :color="inactiveBtnColor">Inativo</v-btn>
                    </v-btn-toggle>
                    <v-select
                        v-if="active === 0"
                        v-model="selectedInactiveReason"
                        :items="inactiveReasons"
                        item-text="text"
                        item-value="id"
                        :rules="inactiveReasonRules"
                        label="Motivo da inativação"
                        outlined
                        class="mt-3"
                        :loading="loadingReasons"
                        :disabled="loadingReasons"
                        required
                    >
                        <template v-slot:no-data>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title v-if="loadingReasons">
                                        Carregando motivos...
                                    </v-list-item-title>
                                    <v-list-item-title v-else>
                                        Nenhum motivo disponível
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                    </v-select>
                </v-col>
                <!-- Dias da janela same-day são definidos no backend:
                     HotelSearchService::SAME_DAY_ELIGIBLE_WEEK_DAYS
                     (app/Services/HotelSearchService.php, segunda a quinta).
                     Se a regra mudar lá, atualizar este label também. -->
                <v-col v-if="isEditingHotel" cols="12" class="can-sell-same-day">
                    <v-checkbox
                        v-model="canSellSameDay"
                        label="Aceitar check-in no mesmo dia (segunda a quinta)"
                        hide-details="auto"
                        class="mt-0"
                    ></v-checkbox>
                </v-col>
                <v-col cols="12" :md="isEditingHotel ? '6' : '4'" :class="isEditingHotel && 'd-flex flex-column align-items-center'">
                    <v-text-field
                        outlined
                        v-model="cnpj"
                        :rules="cnpjRules"
                        label="CNPJ do Hotel"
                        v-mask="'NN.NNN.NNN/NNNN-##'"
                        required
                        :disabled="isEditingHotel && !canUpdateHotelCnpj"
                        class="my-auto cnpj-text-field"
                        :error="cnpjHasAnyError"
                    >
                        <template v-if="canUpdateHotelCnpj" v-slot:append>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }" >
                                    <v-btn
                                        v-on="on"
                                        @click="confirmHotelUpdate"
                                        icon
                                        :loading="awaitingCnpjValidation"
                                        large
                                    >
                                        <v-icon :color="updateHotelCnpjIconColor">
                                            mdi-content-save-edit-outline
                                        </v-icon>
                                    </v-btn>
                                </template>
                                {{ updateHotelCnpjIconTooltipLabel }}
                            </v-tooltip>
                        </template>
                    </v-text-field>
                    <div v-if="cnpjDuplicatedHotelsErrorMessage || hasHotelsWithSameCnpj" class="label-description pt-1">
                        <span class="red--text" v-if="cnpjDuplicatedHotelsErrorMessage">Ocorreu um erro ao restaurar o hotel: {{ cnpjDuplicatedHotelsErrorMessage }}</span>
                        <span class="red--text" v-if="hasHotelsWithSameCnpj">Existem outros hotéis cadastrados com esse CNPJ:</span>
                        <div v-for="hotel in cnpjDuplicatedHotels" :key="hotel.id" class="mt-2">
                            <span>{{ hotel.name }}</span>
                            <template v-if="isEditingHotel || !ownerCreationForm">
                                <v-btn
                                    v-if="!hotel.deleted_at"
                                    color="success"
                                    x-small
                                    @click="editHotel(hotel)"
                                >
                                    Editar
                                </v-btn>
                                <v-btn
                                    v-else
                                    color="success"
                                    x-small
                                    @click="restoreAndEditHotel(hotel)"
                                >
                                    Restaurar e editar
                                </v-btn>
                            </template>
                        </div>
                    </div>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <v-text-field
                        outlined
                        v-model="zipCode"
                        :rules="zipCodeRules"
                        label="CEP"
                        required
                        v-mask="'#####-###'"
                        :loading="addressLoading"
                        :error="isEditingHotel && (!lat || !district)"
                        :error-messages="isEditingHotel && !lat ? 'CEP não encontrado. Redigite para buscar as coordenadas.' : (isEditingHotel && !district ? 'Bairro não encontrado. Redigite o CEP para buscá-lo automaticamente.' : '')"
                    ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                    <v-text-field
                        outlined
                        v-model="state"
                        :rules="stateRules"
                        v-mask="'AA'"
                        label="UF"
                        :disabled="mustDisableAddressField('state')"
                        required
                    ></v-text-field>
                </v-col>
                <v-col cols="12" md="7">
                    <v-text-field
                        ref="cityField"
                        outlined
                        v-model="city"
                        :rules="cityRules"
                        label="Cidade"
                        :disabled="mustDisableCityField()"
                        :hint="addressSearchFailed ? addressSearchFailedHint : ''"
                        :error="addressSearchFailed"
                        @blur="onCityFieldBlur"
                        required
                    ></v-text-field>
                </v-col>
                <v-col cols="12" md="8">
                    <v-text-field
                        outlined
                        v-model="address"
                        :rules="addressRules"
                        label="Logradouro"
                        required
                        hint="Lembre-se de inserir também o número e complemento"
                        :disabled="mustDisableStreetField()"
                        persistent-hint
                    ></v-text-field>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-items-center">
                    <v-text-field
                        outlined
                        v-model="district"
                        :rules="districtRules"
                        :disabled="mustDisableAddressField('district')"
                        label="Bairro"
                        required
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-textarea
                        outlined
                        v-model="description"
                        :rules="descriptionRules"
                        label="Descrição do Hotel"
                        counter="500"
                        required
                    ></v-textarea>
                </v-col>
                <v-col v-if="!ownerCreationForm" cols="12">
                    <v-textarea
                        outlined
                        v-model="note"
                        label="Observações"
                        required
                    ></v-textarea>
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field
                        outlined
                        :hide-details="!$vuetify.breakpoint.smAndDown && 'auto'"
                        v-model="owner"
                        label="Nome do Resposável"
                        :rules="nameRules"
                        required
                        class="my-auto spacing-field-align"
                    ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field
                        outlined
                        :hide-details="!$vuetify.breakpoint.smAndDown && 'auto'"
                        v-model="contact"
                        v-mask="contactMask"
                        :rules="phoneRules"
                        label="Telefone do Responsável"
                        required
                        ref="contact"
                        class="my-auto spacing-field-align"
                    ></v-text-field>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-items-center">
                    <v-text-field
                        outlined
                        :hide-details="!$vuetify.breakpoint.smAndDown && 'auto'"
                        v-model="hotelEmail"
                        :rules="emailRules"
                        label="Email do Responsável"
                        required
                        class="my-auto spacing-field-align"
                    ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                    <span class="label-description"><span class="red--text">* </span>Usaremos esse número para enviar solicitações de reservas e confirmações de pagamento</span>
                    <v-text-field
                        outlined
                        v-model="whatsAppNumber"
                        v-mask="whatsappMask"
                        :rules="whatsappRules"
                        label="WhatsApp do Hotel"
                        ref="whatsAppNumber"
                        class="mt-1"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col v-if="!ownerCreationForm" cols="12" md="4">
                    <v-text-field
                        outlined
                        v-model="secondary_owner"
                        label="Nome do 2º Resposável (opcional)"
                    ></v-text-field>
                </v-col>
                <v-col v-if="!ownerCreationForm" cols="12" md="4">
                    <v-text-field
                        outlined
                        v-model="secondary_contact"
                        v-mask="secondaryContactMask"
                        :rules="optionalPhoneRule"
                        label="2º Telefone do Responsável (opcional)"
                        ref="contact"
                    ></v-text-field>
                </v-col>
                <v-col v-if="!ownerCreationForm" cols="12" md="4">
                    <v-text-field
                        outlined
                        v-model="secondary_email"
                        :rules="secondaryEmailRules"
                        label="2º Email do Responsável (opcional)"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-col v-if="!ownerCreationForm" cols="12">
                <v-row align="center">
                    <v-input :value="rating" :rules="ratingRules">
                        <span>Avaliação:</span>
                        <v-rating
                            v-model="rating"
                            size="32"
                            hover
                            length="5"
                        ></v-rating>
                    </v-input>
                </v-row>
            </v-col>
            <v-row>
                <v-col cols="12" md="6" offset-md="3">
                    <v-btn
                        depressed
                        color="success"
                        @click="validate"
                        class="w-100"
                    >
                        Proximo
                    </v-btn>
                </v-col>
            </v-row>
            <v-dialog width="500" v-model="hotelCnpjUpdateConfirmModal" persistent>
                <v-card>
                    <v-card-title class="warn-card-title">
                        <v-icon size="md" color="white">mdi-alert</v-icon>
                        <span class="ml-2 text-white">Alteração de CNPJ</span>
                    </v-card-title>
                    <v-card-text class="py-4">
                        <template>
                            <v-row>
                                <v-col cols="12">
                                    <div class="pb-2">
                                        O CNPJ é um dado crítico, e sua alteração deve ser cuidadosamente avaliada.
                                    </div>
                                    <span class="font-weight-bold">
                                        Deseja prosseguir?
                                    </span>
                                </v-col>
                            </v-row>
                        </template>
                    </v-card-text>
                    <v-card-actions class="justify-end pb-4">
                        <v-btn color="error" @click="hotelCnpjUpdateConfirmModal = false">Cancelar</v-btn>
                        <v-btn
                            class="ml-2"
                            color="success"
                            :loading="awaitingCnpjUpdate"
                            @click="updateHotelCnpj"
                        >
                            Confirmar
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <response-dialog
                :model="hotelCnpjUpdateResponse.dialog"
                :message="hotelCnpjUpdateResponse.message"
                :type="hotelCnpjUpdateResponse.type"
                @close="closeHotelCnpjUpdateResponseDialogAndReload()"
            />
            <response-dialog
                :model="hasAddressSearchError"
                :message="addressSearchErrorMessage"
                type="error"
                @close="closeAddressSearchErrorDialog()"
            />
        </v-container>
    </v-form>
</template>

<script>

import {isValidCnpj} from "../../../helpers/ValidationRuleHelper";
import {debounce} from "lodash";
import ResponseDialog from "../../Dialog/ResponseDialog.vue";

export default {
    name: "GeneralInfo",
    components: {ResponseDialog},
    props: {
        hotel: {
            type: Object,
            default: null
        },
        ownerCreationForm: {
            type: Boolean,
            default: false
        },
        initialFilledData: {
            type: Object,
            default: null,
        }
    },
    inject: ['ownerCreationForm'],
    data: () => ({
        cityInitialize: false,
        addressInitialize: false,
        addressComponents: {
            zipCode: null,
            state: null,
            city: null,
            district: null,
            address: null,
            number: null,
        },
        haveSetAddressFieldsBasenOnSearch: false,
        cityItems: [],
        addressGoogle: null,
        cityGoogle: null,
        addressLoading: false,
        cityLoading: false,
        valid: false,
        assignedName: '',
        assignedEmail: '',
        hotelName: '',
        active: 1,
        canSellSameDay: false,
        activeBtnColor: 'success',
        inactiveBtnColor: '',
        cnpj: '',
        address: '',
        district: '',
        city: '',
        state: '',
        zipCode: '',
        description: '',
        note: '',
        owner: '',
        secondary_owner: '',
        contact: '',
        secondary_contact: '',
        lat: '',
        lng: '',
        placeId: '',
        hotelEmail: '',
        whatsAppNumber: '',
        secondary_email: '',
        rating: 0,
        hotelBrands: [],
        brand:'',
        nameRules: [
            v => !!v || 'O nome é obrigatorio'
        ],
        addressRules: [
            v => !!v || 'O endereço é obrigatorio'
        ],
        districtRules: [
            v => !!v || 'O bairro é obrigatorio'
        ],
        cityRules: [
            v => !!v || 'A cidade é obrigatoria'
        ],
        stateRules: [
            v => !!v || 'O estado é obrigatorio',
            v => !!v && v.length === 2 || 'Preencha o UF do estado corretamente',
        ],
        descriptionRules: [
            v => !!v || 'A descrição é obrigatória',
            v => (v && v.length <= 500) || 'A descrição deve ter no máximo 500 caracteres'
        ],
        ratingRules: [v => !!v || 'A avaliação é obrigatoria'],
        emailRules: [
            v => !!v || 'O email é obrigatorio',
            v => (!!v && !!v.match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)) || 'O email é inválido'
        ],
        secondaryEmailRules: [
            v => !v || v.match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/) || 'O email é inválido'
        ],
        phoneRules: [
            v => !!v || 'O telefone é obrigatório',
            v => !v || !!v.match(/^\([0-9]{2}\) [0-9]{4,5}-[0-9]{4}$/) || 'Informe um telefone válido'
        ],
        optionalPhoneRule: [
            v => !v || !!v.match(/^\([0-9]{2}\) [0-9]{4,5}-[0-9]{4}$/) || 'Informe um telefone válido'
        ],
        whatsappRules: [
            v => !!v || 'O whatsapp é obrigatório',
            v => !v || !!v.match(/^\([0-9]{2}\) [0-9]{4,5}-[0-9]{4}$/) || 'Informe um whatsapp válido'
        ],
        inactiveReasons: [],
        selectedInactiveReason: null,
        loadingReasons: false,
        cnpjHasValidationError: false,
        cnpjDuplicatedHotels: [],
        cnpjDuplicatedHotelsErrorMessage: '',
        hotelNameErrors: [],
        hotelNameError: false,
        hotelCnpjUpdateConfirmModal: false,
        awaitingCnpjValidation: false,
        awaitingCnpjUpdate: false,
        hotelCnpjUpdateResponse: {
            dialog: false,
            message: '',
            type: null
        },
        addressSearchErrorMessage: null,
        addressSearchFailed: false,
        addressSearchFailedHint: 'CEP não encontrado. Informe a cidade para tentar novamente.',
        isZipCodeSettedWithEditingHotelZipCode: false,
        cnpjMask: 'NN.NNN.NNN/NNNN-##',
        mustValidateCnpjInsertion: false
    }),
    methods: {
        async validateHotelName() {
            if (!this.hotelName) return;

            this.hotelNameErrors = [];

            const response = await axios.get(`/api/restricted-names/check/?string=${encodeURIComponent(this.hotelName)}`);
            if (response?.data?.success === true) {
                this.hotelNameError = false;
            } else {
                this.hotelNameError = true;
                this.hotelNameErrors = [response.data.message];
            }

            this.$nextTick(() => {
                this.$refs.form.inputs.find(input => input.$attrs.label === 'Nome do Hotel')?.validate();
            });
        },
        async validateDuplicatedCnpj() {
            if (this.isCnpjFullyFilled && this.wasCnpjUpdated) {
                this.awaitingCnpjValidation = true;
                await this.makeCnpjValidation();
                this.awaitingCnpjValidation = false;
            }
        },
        googleSearch() {
            this.addressLoading = true;
            axios.get(
                '/api/geocoding/search-address-with-city-placeid',
                {
                    params: this.getGoogleSearchUrlParams(),
                    validateStatus: () => true
                }
            ).then(jsonResult => {
                jsonResult.data.success
                    ? this.onAddressSearchSuccess(jsonResult)
                    : this.onAddressSearchError(jsonResult.data.message);
            }).finally(() => {
                this.addressLoading = false;
            });
        },
        getGoogleSearchUrlParams () {
            let params = {
                address: this.zipCode,
                externalAuthKey: this.externalAuthKey
            };

            if (this.addressSearchFailed && this.city) {
                params.city = this.city;
            }

            return params;
        },
        getHotelBrands(){
            axios.get('/api/hotel-brands')
                .then(brands => {
                    this.hotelBrands = brands.data.brands;
                });
        },
        validate() {
            this.$refs.form.validate();
            if (this.valid) {
                this.$emit('goToTab', 'payment');
            }
        },
        getData() {
            let data = {};
            if (this.ownerCreationForm) {
                data = {...data,
                    "registered_by_name": this.assignedName,
                    "registered_by_email": this.assignedEmail,
                };
            }
            data = {...data,
                "name": this.hotelName,
                "cnpj": this.cnpj,
                "address": this.address,
                "district": this.district,
                "city": this.city,
                "state": this.state,
                "zipCode": this.zipCode,
                "description": this.description,
                "note": this.note,
                "owner": this.owner,
                "secondary_owner": this.secondary_owner,
                "contact": this.contact,
                "whatsapp_number": this.whatsAppNumber,
                "secondary_contact": this.secondary_contact,
                "brand": this.brand,
                "email": this.hotelEmail,
                "secondary_email": this.secondary_email,
                "rating": this.rating,
                "lat": this.lat,
                "lng": this.lng,
                "placeId": this.placeId,
                "active": this.active,
                "can_sell_same_day": this.canSellSameDay,
                "deactivation_reason_id": this.active === 0 ? this.selectedInactiveReason : null
            };
            return data;
        },
        setData(){
            let addressDataFromModel = [{
                formatted_address: this.hotel.address,
                geometry: {
                    location: {
                        lat: this.hotel.latitude,
                        lng: this.hotel.longitude
                    }
                }
            }];

            this.addressComponents = addressDataFromModel;
            this.addressGoogle = addressDataFromModel[0];

            let cityDataFromModel = [{
                description: this.hotel.city,
                place_id: this.hotel.place_id
            }];

            this.cityItems = cityDataFromModel;
            this.cityGoogle = cityDataFromModel[0];

            this.hotelName = this.hotel.name;
            this.cnpj = this.hotel.cnpj;
            this.district = this.hotel.district;
            this.state = this.hotel.state;
            this.zipCode = this.hotel.zip_code;
            this.description = this.hotel.description;
            this.note = this.hotel.note;
            this.hotelBrands = [this.hotel.brand];
            this.brand = this.hotel.brand;
            this.owner = this.hotel.contact_owner;
            this.secondary_owner = this.hotel.secondary_contact_owner;
            this.contact = this.hotel.telephone;
            this.whatsAppNumber = this.hotel.whatsapp_number;
            this.secondary_contact = this.hotel.secondary_telephone;
            this.lat = this.hotel.latitude;
            this.lng = this.hotel.longitude;
            this.placeId = this.hotel.place_id;
            this.hotelEmail = this.hotel.email;
            this.secondary_email = this.hotel.secondary_email;
            this.rating = this.hotel.stars;
            this.active = this.hotel.active;
            this.canSellSameDay = !!this.hotel.can_sell_same_day;
            this.city = this.hotel.city;
            this.address = this.hotel.address;
            this.isZipCodeSettedWithEditingHotelZipCode = true;
            this.selectedInactiveReason = this.hotel.deactivation_reason_id;
        },
        mask (value) {
            let numbers = value !== null && value !== '' ? value.match(/\d/g) : '';
            let numberLength = 0;

            if (numbers) {
                numberLength = numbers.join("").length;
            }

            if (numberLength > 10) {
                return ['(', /[1-9]/, /[1-9]/, ')', ' ', /\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
            } else {
                return ['(', /[1-9]/, /[1-9]/, ')', ' ', /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
            }
        },
        getDeactivationReasons() {
            this.loadingReasons = true;
            axios.get('/api/hotel/deactivation-reasons')
                .then(response => {
                    this.inactiveReasons = response.data.data.map(reason => ({
                        id: reason.id,
                        text: reason.reason
                    }));
                })
                .catch(error => {
                    console.error('Error:', error);
                })
                .finally(() => {
                    this.loadingReasons = false;
                });
        },
        editHotel(hotel) {
            const routeData = this.$router.resolve({name: 'editHotel', params: {id: hotel.id}});
            window.open(routeData.href)
        },
        async restoreAndEditHotel(hotel) {
            this.cnpjDuplicatedHotelsErrorMessage = '';
            await axios.post(`/api/hotel/${hotel.id}/restore`)
                .then(res => {
                    if (res.data.success) {
                        this.editHotel(hotel);
                    } else {
                        throw res;
                    }
                })
                .catch(error => {
                    if (error.response?.data?.message) {
                        this.cnpjDuplicatedHotelsErrorMessage = error.response.data.message;
                    } else {
                        this.cnpjDuplicatedHotelsErrorMessage = 'Ocorreu um erro ao restaurar o hotel.';
                    }
                })
        },
        fillInitialData () {
            if (!this.hotel && this.ownerCreationForm && this.initialFilledData !== null) {
                this.cnpj = this.initialFilledData?.cnpj;
                this.contact = this.initialFilledData?.phone;
                this.hotelName = this.initialFilledData?.name;
                this.hotelEmail = this.initialFilledData?.email;
            }
        },
        getOnlyAplhaNumericChars (value) {
            if (value === null || value === '') {
                return '';
            }
            return value.replace(/[.\/-]/g, '').toUpperCase();
        },
        getMask (value) {
            return this.getOnlyAplhaNumericChars(value).length > 10 ? '(##) #####-####' : '(##) ####-####';
        },
        getCnpjMask () {
            return 'NN.NNN.NNN/NNNN-##';
        },
        confirmHotelUpdate () {
            if (this.isCnpjValidated && this.wasCnpjUpdated) {
                this.hotelCnpjUpdateConfirmModal = true;
            }
        },
        validateCnpj (cnpj) {
            const isValid = !this.mustValidateCnpjInsertion || isValidCnpj(cnpj);
            this.validateCnpjInsertionFromNowOn();
            this.setCnpjHasValidationError(!isValid);
            return isValid;
        },
        validateCnpjInsertionFromNowOn () {
            this.mustValidateCnpjInsertion = true;
        },
        setCnpjHasValidationError (value) {
            this.cnpjHasValidationError = !!value;
        },
        getOnlyCnpjDigits (cnpj) {
            if (!cnpj) {
                return '';
            }

            return cnpj.replace(/[.\/-]/g, '').toUpperCase();
        },
        updateHotelCnpj () {
            this.awaitingCnpjUpdate = true;
            axios.patch('/api/hotel/update-cnpj', {
                hotelId: this.hotel.id,
                cnpj: this.cnpj
            }).then(res => {
                res.data.success ? this.openHotelCnpjSuccessDialog(res) : this.openHotelCnpjErrorDialog(res);
            }).catch(error => {
                this.openHotelCnpjErrorDialog(error);
            }).finally(() => {
                this.awaitingCnpjUpdate = false;
            });
        },
        async makeCnpjValidation () {
            try {
                const cleanedCnpj = this.getOnlyCnpjDigits(this.cnpj);
                const response = await axios.get(`/api/hotel/find-by-cnpj/${cleanedCnpj}`);
                if (response.data && response.data.data.length > 0) {
                    this.cnpjDuplicatedHotels = this.getDuplicatedHotelsWithoutUpdatingHotel(response.data.data);
                    return;
                }
                this.cnpjDuplicatedHotels = [];
            } catch (error) {
                this.cnpjDuplicatedHotels = [];
            }
        },
        openHotelCnpjSuccessDialog (response) {
            this.hotelCnpjUpdateResponse.dialog = true;
            this.hotelCnpjUpdateResponse.message = response.data?.message ?? 'Alteração de CNPJ realizada com sucesso!';
            this.hotelCnpjUpdateResponse.type = 'success';
        },
        openHotelCnpjErrorDialog (response) {
            this.hotelCnpjUpdateResponse.dialog = true;
            this.hotelCnpjUpdateResponse.message = response.data?.message ?? 'Ocorreu um erro durante a alteração de CNPJ!';
            this.hotelCnpjUpdateResponse.type = 'error';
        },
        closeHotelCnpjUpdateResponseDialogAndReload () {
            this.hotelCnpjUpdateResponse.dialog = false;
            this.hotelCnpjUpdateResponse.message = '';
            this.hotelCnpjUpdateResponse.type = null;
            this.hotelCnpjUpdateConfirmModal = false;
            window.location.reload();
        },
        getDuplicatedHotelsWithoutUpdatingHotel (duplicatedHotels) {
            if (this.isEditingHotel) {
                return duplicatedHotels.filter(hotel => hotel.id !== this.hotel.id);
            }
            return duplicatedHotels;
        },
        setAddressItems (googleAddressComponents) {
            this.setZipCodeAndCityBasedOnGoogleSearch(googleAddressComponents);
            this.addressComponents.state = googleAddressComponents.state;
            this.addressComponents.city = googleAddressComponents.city
            this.addressComponents.district = googleAddressComponents.district;
            this.addressComponents.address = googleAddressComponents.street;
            this.addressComponents.number = googleAddressComponents.streetNumber;

            this.state = this.addressComponents.state;
            this.city = this.addressComponents.city
            this.district = this.addressComponents.district;
            this.address = this.addressComponents.address;
            this.number = this.addressComponents.number;
        },
        setZipCodeAndCityBasedOnGoogleSearch (googleAddressComponents) {
            if (!this.addressSearchFailed) {
                this.addressComponents.zipCode = googleAddressComponents.zipCode;
                this.zipCode = this.addressComponents.zipCode;
            }
        },
        mustDisableStreetField () {
            return this.addressSearchFailed;
        },
        mustDisableCityField () {
            return !this.addressSearchFailed;
        },
        mustDisableAddressField (fieldName) {
            if (!this.haveSetAddressFieldsBasenOnSearch) { return true; }
            return !!this.addressComponents[fieldName];
        },
        closeAddressSearchErrorDialog () {
            this.addressSearchErrorMessage = null;
        },
        getOnlyAlphaNumericCharacters (value) {
            if (value === null || value === '') {
                return value;
            }
            return value.replace(/[^\d]+/g, '');
        },
        isZipCodeValid () {
            if (!this.zipCode) return false;
            const zipCodeDigits = this.getOnlyAlphaNumericCharacters(this.zipCode);
            return zipCodeDigits.length && zipCodeDigits.length === 8 && this.zipCode.match('^[0-9]{5}\-[0-9]{3}$');
        },
        onAddressSearchSuccess (jsonResult) {
            const results = jsonResult.data.data;
            const location = results.geometry.location;

            if (!results.addressComponents.zipCode) {
                results.addressComponents.zipCode = this.zipCode;
            }

            this.setAddressItems(results.addressComponents);
            this.lat = location.lat;
            this.lng = location.lng;
            this.placeId = results.cityPlaceId;

            this.haveSetAddressFieldsBasenOnSearch = true;
            this.addressSearchFailed = false;
        },
        onCityFieldBlur () {
            if (this.addressSearchFailed && this.city) {
                this.googleSearch();
            }
        },
        onAddressSearchError (errorMessage) {
            this.addressSearchErrorMessage = errorMessage;
            this.haveSetAddressFieldsBasenOnSearch = false;
            this.cleanAddressItems();
            if (this.$refs.cityField) {
                this.$refs.cityField.focus();
            }
            this.addressSearchFailed = true;
        },
        cleanAddressItems (mustCleanZipCode = false) {
            this.addressComponents.state = null;
            this.addressComponents.city = null;
            this.addressComponents.address = null;
            this.addressComponents.district = null;
            this.addressComponents.number = null;

            if (mustCleanZipCode) {
                this.zipCode = null;
                this.addressComponents.zipCode = null;
            }

            this.address = null;
            this.state = null;
            this.city = null;
            this.district = null;
            this.number = null;
        },
        getPhoneMask (phoneData) {
            if (!phoneData || phoneData.length <= 14) {
                return '(##) ####-####';
            }

            return '(##) #####-####';
        },
    },
    watch: {
        zipCode: {
            handler: debounce(function (val) {
                if (this.isZipCodeValid() && !this.isZipCodeSettedWithEditingHotelZipCode) {
                    this.googleSearch();
                }
                this.isZipCodeSettedWithEditingHotelZipCode = false;
            }, 500)
        },
        active(val) {
            this.activeBtnColor = val ? 'success' : '';
            this.inactiveBtnColor = val ? '' : 'error';
        },
        initialFilledData (newValue) {
            if (newValue) { this.fillInitialData(); }
        },
        cnpj (val) {
            if (val && val !== val.toUpperCase()) {
                this.cnpj = val.toUpperCase();
                return;
            }
            this.cnpjMask = this.getCnpjMask();
            this.validateDuplicatedCnpj();
        }
    },
    computed: {
        inactiveReasonRules() {
            return [
                v => this.active !== 0 || !!v || 'O motivo da inativação é obrigatório quando o status é Inativo'
            ];
        },
        cnpjRules (value) {
          return [
              value => !!value || 'O CNPJ é obrigatorio',
              value => this.validateCnpj(value) || 'O CNPJ é inválido'
          ];
        },
        canUpdateHotelCnpj () {
            return this.isEditingHotel && this.$can('edit.cnpj');
        },
        cnpjHasAnyError () {
            return !!(this.cnpjHasValidationError || this.hasHotelsWithSameCnpj);
        },
        isCnpjValidated () {
            return !this.cnpjHasValidationError && !this.hasHotelsWithSameCnpj;
        },
        updateHotelCnpjIconColor () {
            return (this.isCnpjValidated && this.wasCnpjUpdated) ? 'warning' : 'secondary';
        },
        updateHotelCnpjIconTooltipLabel () {
            if (!this.wasCnpjUpdated) {
                return 'Não existem alterações de CNPJ a serem salvas.';
            }

            return this.isCnpjValidated ?
                'Alterar CNPJ' :
                'Há um erro na validação do CNPJ e por isso não é possível alterá-lo no momento!';
        },
        hasHotelsWithSameCnpj () {
            return this.cnpjDuplicatedHotels && this.cnpjDuplicatedHotels.length;
        },
        wasCnpjUpdated () {
            if (!this.isEditingHotel) { return this.isCnpjFullyFilled; }
            return this.getOnlyCnpjDigits(this.cnpj) !== this.getOnlyCnpjDigits(this.hotel.cnpj);
        },
        isCnpjFullyFilled () {
            if (this.cnpj === null) {
                return false;
            }

            return this.getOnlyCnpjDigits(this.cnpj).length === 14;
        },
        isEditingHotel () {
            return !!this.hotel;
        },
        hasAddressSearchError () {
            return this.addressSearchErrorMessage !== null && this.addressSearchErrorMessage !== '';
        },
        zipCodeRules (val) {
            return [
                val => !!val || 'O CEP é obrigatorio',
                val => this.isZipCodeValid() || 'O CEP é inválido'
            ];
        },
        externalAuthKey () {
            return this.$route.params.externalId;
        },
        contactMask () {
            return this.getPhoneMask(this.contact);
        },
        whatsappMask () {
            return this.getPhoneMask(this.whatsAppNumber);
        },
        secondaryContactMask () {
            return this.getPhoneMask(this.secondary_contact);
        }
    },
    created() {
        if (this.hotel !== null) {
            this.setData();
        }
        this.getHotelBrands();
        this.getDeactivationReasons();
    },
}
</script>

<style scoped>
.active {
    position: relative;
}

.active-label {
    position: absolute;
    top: 0;
    left: 20px;
    font-size: 14px;
    color: rgba(0,0,0,0.6);
    z-index: 99;
}

.title-class {
    font-size: 1.35rem;
    color: rgba(0,0,0,0.6);
}

.btnToggle {
    width: 100%;
}

.spacing-field-align {
    @media screen and (min-width: 960px) {
        padding-top: 9px;
        padding-bottom: 9px;
    }
}

.label-description {
    font-size: 12px;
    color: rgba(0,0,0,0.8);
}

.warn-card-title {
    background-color: #fb8c00;
    color: white;
    font-weight: bold;
}

.cnpj-text-field >>> .v-input__append-inner {
    margin-top: 6px !important;
}
</style>
